import {createAction, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Ecard, RecipientMessages} from '~/QuickEcards/types/ecard';
import {MessagesType} from '~/QuickEcards/types/misc';

type RecipientMessagesState = {
  recipientMessages: RecipientMessages;
  globalMessage: string;
  selectedRecipient: string | null;
  recipientFilter: string;
  isMessageEditorOn: boolean;
  selectedTab: MessagesType | null;
  hasUsedCustomMessages: boolean;
  hasUsedGlobalMessage: boolean;
  ready: boolean;
};

const initialState: RecipientMessagesState = {
  recipientMessages: {},
  globalMessage: '',
  selectedRecipient: null,
  recipientFilter: '',
  isMessageEditorOn: false,
  selectedTab: null,
  hasUsedCustomMessages: false,
  hasUsedGlobalMessage: false,
  ready: false,
};

interface InitializeRecipientMessagesPayload {
  recipientMessages: RecipientMessages;
  numGuests: number;
}

const loadEcardAction = createAction('ecard/loadEcard');

const recipientMessagesSlice = createSlice({
  name: 'recipientMessages',
  initialState,
  reducers: {
    initializeRecipientMessages: (
      state,
      action: PayloadAction<InitializeRecipientMessagesPayload>
    ) => {
      const recipientMessages = {...state.recipientMessages, ...action.payload.recipientMessages};
      const hasUsedCustomMessages = Object.keys(recipientMessages).length > 0;
      return {
        ...state,
        recipientMessages,
        hasUsedCustomMessages,
        hasUsedGlobalMessage: Boolean(state.globalMessage),
        selectedTab:
          state.globalMessage || (action.payload.numGuests > 1 && !hasUsedCustomMessages)
            ? MessagesType.GLOBAL
            : MessagesType.CUSTOM,
      };
    },
    updateSelectedTab: (state, action: PayloadAction<MessagesType | null>) => {
      const selectedRecipient: string | null =
        action.payload === MessagesType.CUSTOM
          ? (Object.keys(state.recipientMessages).find((k) => state.recipientMessages[k]) ?? null)
          : null;
      return {
        ...state,
        selectedRecipient,
        selectedTab: action.payload,
      };
    },
    updateGlobalMessage: (state, action: PayloadAction<string>) => ({
      ...state,
      globalMessage: action.payload,
    }),
    updateIndividualMessage: (state, action: PayloadAction<RecipientMessages>) => ({
      ...state,
      recipientMessages: {...state.recipientMessages, ...action.payload},
    }),
    setHasUsedCustomMessages: (state, action: PayloadAction<boolean>) => ({
      ...state,
      hasUsedCustomMessages: action.payload,
    }),
    setHasUsedGlobalMessage: (state, action: PayloadAction<boolean>) => ({
      ...state,
      hasUsedGlobalMessage: action.payload,
    }),
    removeIndividualMessage: (state, action: PayloadAction<string>) => {
      const recipientMessages = {...state.recipientMessages};
      delete recipientMessages[action.payload];
      return {
        ...state,
        recipientMessages,
      };
    },
    setSelectedRecipient: (state, action: PayloadAction<string | null>) => ({
      ...state,
      selectedRecipient: action.payload,
    }),
    setIsMessageEditorOn: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isMessageEditorOn: action.payload,
    }),
    setRecipientFilter: (state, action: PayloadAction<string>) => ({
      ...state,
      recipientFilter: action.payload,
    }),
    propagateStepChange: (state) => ({
      ...state,
      recipientFilter: '',
    }),
    leaveRecipientMessagesPage: (state) => ({
      ...state,
      selectedRecipient: null,
    }),
    setIsMessagesControllerReady: (state, action: PayloadAction<boolean>) => ({
      ...state,
      ready: action.payload,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadEcardAction, (state, action: PayloadAction<Partial<Ecard> | undefined>) => {
        // eslint-disable-next-line no-param-reassign
        state.globalMessage =
          action.payload?.event_data?.options.write.text_layer_back ?? state.globalMessage;
      })
      .addDefaultCase(() => {});
  },
  selectors: {
    selectMessage: ({selectedRecipient, globalMessage, recipientMessages}, fallback?: string) => {
      const message = window.customMessage || globalMessage || fallback || null;
      if (!selectedRecipient) return message;
      return recipientMessages[selectedRecipient] ?? message;
    },
    selectSelectedTab: (state) => state.selectedTab,
    selectRecipientMessages: (state) => state.recipientMessages,
    selectRecipientMessage: (state, guestId: string) => state.recipientMessages[guestId],
    selectHasCustomMessages: (state) => Object.values(state.recipientMessages).some(Boolean),
    selectHasUsedCustomMessages: (state) => state.hasUsedCustomMessages,
    selectHasUsedGlobalMessage: (state) => state.hasUsedGlobalMessage,
    selectSelectedRecipient: (state) => state.selectedRecipient,
    selectIsMessageEditorOn: (state) => state.isMessageEditorOn,
    selectGlobalMessage: (state) => state.globalMessage,
    selectRecipientFilter: (state) => state.recipientFilter,
    selectIsMessagesControllerReady: (state) => state.ready,
  },
});

export const {
  initializeRecipientMessages,
  updateSelectedTab,
  updateGlobalMessage,
  updateIndividualMessage,
  setHasUsedCustomMessages,
  setHasUsedGlobalMessage,
  removeIndividualMessage,
  setSelectedRecipient,
  setIsMessageEditorOn,
  setRecipientFilter,
  propagateStepChange,
  leaveRecipientMessagesPage,
  setIsMessagesControllerReady,
} = recipientMessagesSlice.actions;
export const {
  selectMessage,
  selectSelectedTab,
  selectRecipientMessages,
  selectRecipientMessage,
  selectHasCustomMessages,
  selectHasUsedCustomMessages,
  selectHasUsedGlobalMessage,
  selectSelectedRecipient,
  selectIsMessageEditorOn,
  selectGlobalMessage,
  selectRecipientFilter,
  selectIsMessagesControllerReady,
} = recipientMessagesSlice.selectors;
export const recipientMessagesReducer = recipientMessagesSlice.reducer;
